/**
 * Settings — Ayarlar sayfası (iter51 profesyonel yeniden tasarım)
 *
 * Sol tarafta section navigation, sağda seçili section'un içeriği. Her
 * kullanıcı Profil / Güvenlik / Bildirimler / Görünüm'e erişebilir;
 * Organizasyon, Milestone Şablonu ve Smartsheet sadece super_admin'de.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../store/authStore";
import ProfileSection from "./settings/ProfileSection";
import SecuritySection from "./settings/SecuritySection";
import NotificationPreferences from "./settings/NotificationPreferences";
import OrganizationSection from "./settings/OrganizationSection";
import MilestoneTemplateEditor from "./settings/MilestoneTemplateEditor";
import SmartsheetIntegration from "./settings/SmartsheetIntegration";
import EmailsSection from "./settings/EmailsSection";
import {
  User,
  KeyRound,
  Bell,
  Building2,
  Layers,
  FileSpreadsheet,
  Mail,
  ChevronRight,
} from "lucide-react";

/**
 * Sections registry — keep this list in sync with the switch below.
 * `super` flag hides the section for non-super_admin users.
 */
const SECTIONS = [
  { key: "profile", label: "Profil", icon: User, hint: "Kişisel bilgilerin" },
  { key: "security", label: "Güvenlik", icon: KeyRound, hint: "Şifre & oturum" },
  {
    key: "notifications",
    label: "Bildirimler",
    icon: Bell,
    hint: "E-posta özet & nudge",
  },
  {
    key: "organization",
    label: "Organizasyon",
    icon: Building2,
    hint: "Şirket adı & saat dilimi",
    super: true,
  },
  {
    key: "milestones",
    label: "Milestone Şablonu",
    icon: Layers,
    hint: "İnşaat şablonu",
    super: true,
  },
  {
    key: "smartsheet",
    label: "Smartsheet",
    icon: FileSpreadsheet,
    hint: "Sync & entegrasyon",
    super: true,
  },
  {
    key: "emails",
    label: "Mailler",
    icon: Mail,
    hint: "Şablonlar & test gönderimi",
    super: true,
  },
];

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isSuper = ["super_admin", "ultra_admin"].includes(user?.role);
  const [active, setActive] = useState("profile");

  const visibleSections = SECTIONS.filter((s) => !s.super || isSuper);
  const current = visibleSections.find((s) => s.key === active) || visibleSections[0];

  const renderSection = () => {
    switch (current.key) {
      case "profile":
        return <ProfileSection />;
      case "security":
        return <SecuritySection />;
      case "notifications":
        return <NotificationPreferences />;
      case "organization":
        return <OrganizationSection />;
      case "milestones":
        return <MilestoneTemplateEditor />;
      case "smartsheet":
        return <SmartsheetIntegration />;
      case "emails":
        return <EmailsSection />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          {t("brand")}
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("settings.title")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Profilini, güvenlik ayarlarını ve entegrasyonlarını yönet.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
        {/* Sidebar navigation */}
        <nav
          className="lg:sticky lg:top-4 lg:self-start"
          data-testid="settings-nav"
          aria-label="Ayarlar bölümleri"
        >
          <ul className="space-y-0.5">
            {visibleSections.map((s) => {
              const Icon = s.icon;
              const isActive = current.key === s.key;
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    onClick={() => setActive(s.key)}
                    data-testid={`settings-nav-${s.key}`}
                    className={[
                      "group flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors",
                      isActive
                        ? "bg-zinc-900 text-white shadow-sm"
                        : "text-zinc-700 hover:bg-zinc-100",
                    ].join(" ")}
                  >
                    <Icon
                      className={[
                        "h-4 w-4 shrink-0",
                        isActive ? "text-white" : "text-zinc-500 group-hover:text-zinc-900",
                      ].join(" ")}
                      strokeWidth={2}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">
                        {s.label}
                      </span>
                      <span
                        className={[
                          "block text-[11px] truncate",
                          isActive ? "text-zinc-300" : "text-zinc-500",
                        ].join(" ")}
                      >
                        {s.hint}
                      </span>
                    </span>
                    <ChevronRight
                      className={[
                        "h-3.5 w-3.5 shrink-0 transition-opacity",
                        isActive ? "text-white/70" : "text-zinc-300 opacity-0 group-hover:opacity-100",
                      ].join(" ")}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Content pane — each section brings its own card. */}
        <div data-testid={`settings-content-${current.key}`}>
          {renderSection()}
        </div>
      </div>
    </div>
  );
}
